// Phase 44.28 — Schermata "Pagamento confermato → istruzioni etichetta".
//
// Mostrata dopo il pagamento Stripe (success_url backend punta qui).
// Goal UX: forzare l'utente a vedere SUBITO le istruzioni di identificazione
// pacco prima di entrare nella OrderDetail. Pattern app-native: hero check
// di conferma → istruzioni con CTA grande "Fatto, vai all'ordine".
//
// Flusso:
//   1. POST /orders/{id}/payment/checkout-session
//   2. Stripe Checkout
//   3. Redirect a /app/orders/{id}/label?session_id=cs_xxx (success_url)
//   4. Mostra ParcelLabelCard + button "Eseguito"
//   5. Click "Eseguito" → /app/orders/{id} (replace history)

import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { useCheckoutReturn } from "@/lib/useCheckoutReturn";
import { orderPaymentBlock } from "@/lib/orderPaymentStatus";
import CheckoutVerification from "@/components/CheckoutVerification";
import { api } from "@/lib/api";
import ParcelLabelCard from "@/components/ParcelLabelCard";

export default function LabelInstructionsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const verification = useCheckoutReturn({
    sessionId: `${id}:${sessionId || "direct"}`,
    request: async () => {
      if (sessionId) await api.post(`/orders/${id}/payment/refresh`, { session_id: sessionId });
      return (await api.get(`/orders/${id}`)).data;
    },
    isComplete: data => data.payment_status === "paid",
    getBlock: orderPaymentBlock,
    isTerminal: data => !sessionId || ["expired", "failed", "cancelled"].includes(data.payment_status),
  });
  const paymentConfirmed = verification.status === "complete";
  const goToOrder = () => navigate(`/app/orders/${id}${!paymentConfirmed && sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ""}`, { replace: true });

  if (!paymentConfirmed) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto" data-testid="label-instructions-pending">
        <CheckoutVerification verification={verification} testId="label-payment-verification" />
        <button className="btn-ghost" type="button" onClick={goToOrder}>Vai all’ordine</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="label-instructions-page">
      {/* Hero conferma pagamento */}
      <section className="relative overflow-hidden card-soft p-6 sm:p-8 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/80 via-white to-white">
        <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-emerald-100/60 blur-2xl pointer-events-none" aria-hidden />
        <div className="relative flex items-start gap-4">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/30 shrink-0">
            <CheckCircle2 className="h-7 w-7" strokeWidth={2.5} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="overline text-emerald-700">Pagamento confermato</p>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-emerald-950 leading-tight mt-1">
              Consegna attivata
            </h1>
            <p className="text-sm text-emerald-900/80 mt-2 leading-relaxed">
              Il pagamento è confermato. Segui lo stato della spedizione nei dettagli dell'ordine.
              Un ultimo passaggio: <strong>identifica il pacco</strong> prima del ritiro.
            </p>
          </div>
        </div>
      </section>

      {/* Istruzioni etichetta (riusa il componente single-source) */}
      <ParcelLabelCard orderId={id} testid="label-instructions-card" />

      {/* CTA "Eseguito" — sticky bottom area */}
      <section className="card-soft p-5 sm:p-6 bg-dapnow-ink text-white space-y-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white shrink-0">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-bold tracking-tight">Hai scritto numero e righe firma sul pacco?</p>
            <p className="text-xs text-white/70 mt-0.5 leading-relaxed">
              Quando il pacco è pronto, conferma qui sotto. Andremo alla schermata ordine dove vedrai lo stato live della consegna.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={goToOrder}
          data-testid="label-instructions-done-btn"
          className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-emerald-500 text-white font-bold tracking-tight hover:bg-emerald-400 active:scale-[0.99] transition shadow-lg shadow-emerald-500/20"
        >
          <CheckCircle2 className="h-5 w-5" strokeWidth={2.5} />
          Eseguito, vai all'ordine
          <ArrowRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={goToOrder}
          data-testid="label-instructions-skip-btn"
          className="w-full text-center text-xs font-semibold text-white/60 hover:text-white/80 transition py-2"
        >
          Lo farò più tardi
        </button>
      </section>
    </div>
  );
}
